import { useTranslation } from 'react-i18next';
import { useQueryClient, useMutation } from '@tanstack/react-query';
import { themeGet } from '@styled-system/theme-get';
import styled from 'styled-components';
import i18n from '../../../app/i18n';

import { repickWinner, deleteWinner } from '../data';
import Text from '../../../shared/Text/Text';
import Card from '../../../shared/Card';
import { RightSideSection } from './StyledComponents';
import {
  Table,
  Tr,
  Th,
  ShadedTr,
  ThContentWrapper,
} from '../../../shared/TableComponents/StyledComponents';
import Sort from '../../../shared/Sort';

const TextBox = styled.div`
  margin: 9px 24px 24px;
`;

const TableSection = styled.div`
  padding: 9px 0px 5px;
`;

const Td = styled.td`
  padding: 0 15px 0 0;
  border-top: 1px solid ${themeGet('colors.black.10')};
  border-bottom: none !important;
  color: ${themeGet('colors.black.100')};
  font-size: 12px;
  padding-right: 24px;
  &:first-child {
    padding: 0px 24px;
  }
`;

const TdRight = styled(Td)`
  padding-right: 60px;
  text-align: right;
`;

const ColumnTitle = styled.p`
  padding: 0 0 0 8px;
  margin: 0;
`;

const StyledTh = styled(Th)`
  padding: ${({ columnName }) =>
    columnName === 'name'
      ? '0 15px 12px 24px !important'
      : '0 60px 12px 0 !important'};
`;

const ThContent = styled(ThContentWrapper)`
  cursor: ${({ sortEnabled }) => (sortEnabled ? 'pointer' : 'default')};
  justify-content: ${({ columnName }) =>
    columnName === 'tickets' ? 'flex-end' : 'flex-start'};
`;

const ActionTd = styled.td`
  border-top: 1px solid ${themeGet('colors.black.10')};
  text-align: right;
  border-bottom: none !important;
`;

const Email = styled.a`
  font-weight: 400;
  font-size: 12px;
  color: ${themeGet('colors.primary.100')};
`;

const ActionBtn = styled.a`
  padding: 8px 0 8px 24px;
  color: ${themeGet('colors.primary.100')};
  font-weight: 400;
  font-size: 12px;

  :last-child {
    padding-right: 30px;
  }
`;

const CardWinners = styled(Card)`
  margin-top: 0px;
  margin-right: 0px;
  @media (max-width: 900px) {
    margin: 24px 0px;
  }
`;

const tableColumns = [
  { name: 'name', title: i18n.t('prizes:pickWinners.name'), sort: true },
  { name: 'tickets', title: i18n.t('prizes:pickWinners.chances'), sort: true },
  { name: 'email', title: i18n.t('prizes:pickWinners.email'), sort: true },
];

const TableHead = ({ sortingStatus, changeSort }) => (
  <thead>
    <Tr>
      {tableColumns.map(column => (
        <StyledTh key={column.name} columnName={column.name}>
          <ThContent
            sortEnabled={column.sort}
            columnName={column.name}
            onClick={() => column.sort && changeSort(column.name)}
          >
            {column.sort && (
              <Sort sortingStatus={sortingStatus} columnName={column.name} />
            )}
            <ColumnTitle>{column.title}</ColumnTitle>
          </ThContent>
        </StyledTh>
      ))}
    </Tr>
  </thead>
);

const WinnersTable = ({ raffle, sortingStatus, changeSort }) => {
  const { t } = useTranslation('prizes');
  const queryClient = useQueryClient();

  const mutationSideEffects = {
    onSuccess: () => {
      queryClient.invalidateQueries(['getRaffle']);
    },
  };

  const { mutate: repickWinnerMutation } = useMutation(
    repickWinner,
    mutationSideEffects,
  );

  const { mutate: deleteWinnerMutation } = useMutation(
    deleteWinner,
    mutationSideEffects,
  );

  return (
    <RightSideSection>
      <CardWinners title={t('pickWinners.tableTitle')}>
        {raffle.winners.length ? (
          <TableSection>
            <Table>
              <colgroup>
                <col span="1" style={{ width: '30%' }}></col>
                <col span="1" style={{ width: '15%' }}></col>
                <col span="1" style={{ width: '25%' }}></col>
                <col span="1" style={{ width: '30%' }}></col>
              </colgroup>
              <TableHead
                sortingStatus={sortingStatus}
                changeSort={changeSort}
              />
              <tbody>
                {raffle.winners.map(row => {
                  return (
                    <ShadedTr key={row.uuid}>
                      <Td>{row.name}</Td>
                      <TdRight>{row.tickets}</TdRight>
                      <Td>
                        <Email href={`mailto:${row.email}`}>{row.email}</Email>
                      </Td>
                      <ActionTd>
                        <ActionBtn
                          onClick={() =>
                            repickWinnerMutation({ userId: row.uuid })
                          }
                        >
                          {t('pickWinners.repick')}
                        </ActionBtn>
                        <ActionBtn
                          onClick={() =>
                            deleteWinnerMutation({ userId: row.uuid })
                          }
                        >
                          {t('pickWinners.delete')}
                        </ActionBtn>
                      </ActionTd>
                    </ShadedTr>
                  );
                })}
              </tbody>
            </Table>
          </TableSection>
        ) : (
          <TextBox>
            {raffle.status === 'pick_winners' ? (
              <Text as="span" variant="small2">
                {t('pickWinners.configureWinners')}
              </Text>
            ) : (
              <Text as="span" variant="small2">
                {t('pickWinners.noWinners')}
              </Text>
            )}
          </TextBox>
        )}
      </CardWinners>
    </RightSideSection>
  );
};

export default WinnersTable;
